import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { notificationAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import {
    Shield, Bell, Monitor, ExternalLink, ChevronRight,
    Mail, Lock, Trash2, Check, Loader, ToggleLeft, ToggleRight
} from 'lucide-react';

const PREFS_KEY = 'tdw_playback_prefs';

const defaultPrefs = {
    autoplayVideos: false,
    muteByDefault: true,
};

const Settings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [resetSent, setResetSent] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');

    const [prefs, setPrefs] = useState(() => {
        try {
            return { ...defaultPrefs, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
        } catch {
            return defaultPrefs;
        }
    });

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [notifTypes, setNotifTypes] = useState([]);
    const [loadingNotifTypes, setLoadingNotifTypes] = useState(true);

    useEffect(() => {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    }, [prefs]);

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const res = await notificationAPI.getPreferences();
                if (res.success) setNotifTypes(res.data);
            } catch (err) {
                console.error('Error loading notification preferences:', err);
            } finally {
                setLoadingNotifTypes(false);
            }
        };
        loadPreferences();
    }, []);

    const toggleNotifType = async (typeKey, isEnabled) => {
        setNotifTypes(prev => prev.map(t => (t.type_key === typeKey ? { ...t, is_enabled: isEnabled } : t)));
        try {
            await notificationAPI.updatePreference(typeKey, isEnabled);
        } catch (err) {
            console.error('Error updating notification preference:', err);
        }
    };

    const togglePref = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

    const handleSendReset = async () => {
        if (!user?.email) return;
        setResetLoading(true);
        setResetError('');
        try {
            await api.post('/auth/forgot-password', { email: user.email });
            setResetSent(true);
        } catch {
            setResetError('Failed to send reset email. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const Toggle = ({ value, onToggle }) => (
        <button
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                value ? 'bg-blue-600' : 'bg-gray-600'
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    value ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    );

    return (
        <div className="min-h-screen bg-gray-950">
            <PageHeader
                title="Settings"
                subtitle="Manage your account preferences"
            />

            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">

                {/* Quick Links */}
                <div className="glass-card divide-y divide-gray-700/50">
                    {[
                        { to: '/edit-profile', label: 'Edit Profile', sub: 'Update your name, bio, photo and links', icon: '🌊' },
                        { to: '/account', label: 'Account & Billing', sub: 'View subscription and transaction history', icon: '💳' },
                        { to: '/subscriptions', label: 'Subscriptions', sub: 'Manage your membership plan', icon: '⭐' },
                    ].map(({ to, label, sub, icon }) => (
                        <Link
                            key={to}
                            to={to}
                            className="flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors group"
                        >
                            <div className="flex items-center gap-4">
                                <span className="text-xl">{icon}</span>
                                <div>
                                    <p className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">{label}</p>
                                    <p className="text-xs text-gray-400">{sub}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-blue-400 transition-colors" />
                        </Link>
                    ))}
                </div>

                {/* Password */}
                <div className="glass-card p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Lock className="w-5 h-5 text-blue-400" />
                        <h2 className="text-base font-semibold text-white">Password</h2>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">
                        We'll email a reset link to <span className="text-gray-200">{user?.email}</span>.
                    </p>
                    {resetSent ? (
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                            <Check className="w-4 h-4" />
                            Reset link sent — check your inbox.
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={handleSendReset}
                                disabled={resetLoading}
                                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                            >
                                {resetLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                                Send password reset email
                            </button>
                            {resetError && <p className="mt-2 text-xs text-red-400">{resetError}</p>}
                        </>
                    )}
                </div>

                {/* Notifications */}
                <div className="glass-card p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Bell className="w-5 h-5 text-blue-400" />
                        <h2 className="text-base font-semibold text-white">Notifications</h2>
                    </div>
                    {loadingNotifTypes ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                    ) : (
                        <div className="space-y-4">
                            {notifTypes.map((t) => (
                                <div key={t.type_key} className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-white">{t.label}</p>
                                        <p className="text-xs text-gray-400">{t.description}</p>
                                    </div>
                                    <Toggle value={!!t.is_enabled} onToggle={() => toggleNotifType(t.type_key, !t.is_enabled)} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Playback Preferences */}
                <div className="glass-card p-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Monitor className="w-5 h-5 text-blue-400" />
                        <h2 className="text-base font-semibold text-white">Playback</h2>
                    </div>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-white">Autoplay videos</p>
                                <p className="text-xs text-gray-400">Start playing when you hover over a video</p>
                            </div>
                            <Toggle value={prefs.autoplayVideos} onToggle={() => togglePref('autoplayVideos')} />
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-white">Mute videos by default</p>
                                <p className="text-xs text-gray-400">Start all videos without sound</p>
                            </div>
                            <Toggle value={prefs.muteByDefault} onToggle={() => togglePref('muteByDefault')} />
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="glass-card p-5 border border-red-500/20">
                    <div className="flex items-center gap-3 mb-4">
                        <Trash2 className="w-5 h-5 text-red-400" />
                        <h2 className="text-base font-semibold text-white">Danger Zone</h2>
                    </div>
                    {!showDeleteConfirm ? (
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-white">Delete account</p>
                                <p className="text-xs text-gray-400">Permanently remove your account and all your content</p>
                            </div>
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="px-4 py-2 text-sm text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/10 transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-red-300">
                                This action is permanent and cannot be undone. All your uploads, likes, and profile data will be deleted.
                            </p>
                            <p className="text-sm text-gray-400">
                                To delete your account, please contact us at{' '}
                                <a href="mailto:support@thedeepwave.com" className="text-blue-400 hover:underline">
                                    support@thedeepwave.com
                                </a>
                            </p>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="text-sm text-gray-400 hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Settings;
